import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Alert,
  Divider, // Import Divider
  Dialog, // Import Dialog components
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { callHttpFunction } from "./firestoreService";

function Support() {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  // --- NEW: State for the confirmation dialog ---
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject || !message) {
      setError(t("support.fillAllFields"));
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const data = { subject, message };
      const result = await callHttpFunction("sendSupportRequest", data);

      if (result.success) {
        setSuccess(t("support.requestSent"));
        setSubject("");
        setMessage("");
      } else {
        throw new Error(result.error || "An unknown error occurred.");
      }
    } catch (err) {
      setError(`Failed to send request: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // --- NEW: Function to handle the deletion request ---
  const handleDeletionRequest = async () => {
    setIsConfirmOpen(false); // Close the dialog
    setLoading(true);
    setError("");
    setSuccess("");

    // Pre-fill and send a specific support request for deletion
    const deletionSubject = "Account Deletion Request";
    const deletionMessage =
      "Please proceed with the permanent deletion of my account and all associated data as per your data policy. I confirm that I understand this action is irreversible.";

    try {
      const data = { subject: deletionSubject, message: deletionMessage };
      const result = await callHttpFunction("sendSupportRequest", data);

      if (result.success) {
        setSuccess(t("support.deletionRequestSent"));
        setSubject(""); // Clear form in case they want to do something else
        setMessage("");
      } else {
        throw new Error(result.error || "An unknown error occurred.");
      }
    } catch (err) {
      setError(`Failed to send deletion request: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Container maxWidth="md">
        <Typography variant="h4" component="h1" gutterBottom>
          {t("support.title")}
        </Typography>
        <Typography
          variant="body1"
          component="p"
          sx={{ mb: 2 }}
          color="text.secondary"
        >
          {t("support.description")}
        </Typography>

        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          {/* ... existing TextFields for subject and message ... */}
          <TextField
            margin="normal"
            required
            fullWidth
            id="subject"
            label={t("support.subject")}
            name="subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="message"
            label={t("contact.message")}
            id="message"
            multiline
            rows={8}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {loading && !isConfirmOpen ? (
              <CircularProgress size={24} />
            ) : (
              t("support.submitRequest")
            )}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {success}
          </Alert>
        )}

        {/* --- NEW: Account Deletion Section --- */}
        <Divider sx={{ my: 4 }} />
        <Box
          sx={{
            p: 2,
            border: "1px solid",
            borderColor: "error.main",
            borderRadius: 1,
          }}
        >
          <Typography variant="h6" component="h2" color="error.main">
            {t("support.accountDeletion")}
          </Typography>
          <Typography variant="body2" component="p" sx={{ mt: 1, mb: 2 }}>
            {t("support.deletionDescription")}
          </Typography>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setIsConfirmOpen(true)}
            disabled={loading}
          >
            {t("support.requestDeletion")}
          </Button>
        </Box>
      </Container>

      {/* --- NEW: Confirmation Dialog --- */}
      <Dialog open={isConfirmOpen} onClose={() => setIsConfirmOpen(false)}>
        <DialogTitle>{t("support.confirmDeletionTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("support.confirmDeletionMessage")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsConfirmOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleDeletionRequest} color="error" autoFocus>
            {t("support.yesDeleteAccount")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Support;
